import { IPage, MenuService } from '@shared/core';
import { IAccordionPanel } from '@shared/ui';

export const FAQ_MENU_SLUG = 'faq';

/**
 * i18n key pairs for the FAQ accordion. The question/answer text lives in the
 * i18n JSON as FAQ_Q* / FAQ_A* keys
 * (libs/shared/assets/src/lib/i18n/{ru,kz,en}.json). The FAQ components resolve
 * these keys to the active language and rebuild on language change.
 */
export const FAQ_KEYS: { header: string; content: string }[] = [
  { header: 'FAQ_Q1', content: 'FAQ_A1' },
  { header: 'FAQ_Q2', content: 'FAQ_A2' },
  { header: 'FAQ_Q3', content: 'FAQ_A3' },
  { header: 'FAQ_Q4', content: 'FAQ_A4' },
  { header: 'FAQ_Q5', content: 'FAQ_A5' },
  { header: 'FAQ_Q6', content: 'FAQ_A6' },
  { header: 'FAQ_Q7', content: 'FAQ_A7' },
  { header: 'FAQ_Q8', content: 'FAQ_A8' },
  { header: 'FAQ_Q9', content: 'FAQ_A9' },
];

export interface IFaqContent {
  intro: string;
  panels: IAccordionPanel[];
}

export interface IFaqBlock {
  text: string;
  html: string;
  isQuestion: boolean;
}

const NUMBERED_QUESTION = /^\d+\s*[.)]/;

/** Страница CMS, если она не скрыта: скрыта только `isVisible === false`, как в `MenuService.filterVisible`. */
export function visibleCmsPage<T extends { isVisible?: boolean }>(
  page: T | null | undefined,
): T | undefined {
  return page && page.isVisible !== false ? page : undefined;
}

export function getVisibleFaqPage(menuService: MenuService): IPage | undefined {
  return visibleCmsPage(menuService.getMenuBySlug(FAQ_MENU_SLUG)?.page);
}

/** Fallback-вопросы из i18n (`FAQ_KEYS`), пока в CMS нет страницы FAQ; непереведённый ключ — как есть. */
export function faqKeyPanels(translate: (key: string) => string | undefined): IAccordionPanel[] {
  return FAQ_KEYS.map(key => ({
    header: translate(key.header) ?? key.header,
    content: translate(key.content) ?? key.content,
  }));
}

export function parseFaqContent(html: string): IFaqContent {
  return groupFaqBlocks(toFaqBlocks(new DOMParser().parseFromString(html, 'text/html').body));
}

/**
 * If the questions are numbered, a bold block without a number (e.g. «(Для Приложения)»)
 * is a subheading inside an answer, not a question.
 */
export function groupFaqBlocks(sourceBlocks: IFaqBlock[]): IFaqContent {
  const numbered = sourceBlocks.some(
    block => block.isQuestion && NUMBERED_QUESTION.test(block.text),
  );
  const blocks = numbered
    ? sourceBlocks.map(block => ({
        ...block,
        isQuestion: block.isQuestion && NUMBERED_QUESTION.test(block.text),
      }))
    : sourceBlocks;
  const result: IFaqContent = { intro: '', panels: [] };
  let current: IAccordionPanel | undefined;

  blocks.forEach((block, index) => {
    const next = blocks[index + 1];
    if (block.isQuestion && next && !next.isQuestion) {
      current = { header: block.text, content: '', contentHtml: '' };
      result.panels.push(current);
    } else if (current) {
      current.content += (current.content ? '\n' : '') + block.text;
      current.contentHtml += block.html;
    } else {
      result.intro += block.html;
    }
  });

  return result;
}

function toFaqBlocks(body: HTMLElement): IFaqBlock[] {
  return Array.from(body.childNodes)
    .map(node => {
      const text = normalizeSpaces(node.textContent ?? '');
      if (!text) {
        return undefined;
      }
      if (!(node instanceof Element)) {
        const paragraph = body.ownerDocument.createElement('p');
        paragraph.textContent = text;
        return { text, html: paragraph.outerHTML, isQuestion: false };
      }
      return { text, html: normalizeSpaces(node.outerHTML), isQuestion: isFaqQuestion(node, text) };
    })
    .filter((block): block is IFaqBlock => !!block);
}

function isFaqQuestion(node: Element, text: string): boolean {
  if (/^H[1-6]$/.test(node.tagName)) {
    return true;
  }
  const boldText = Array.from(node.querySelectorAll('strong, b'))
    .map(bold => normalizeSpaces(bold.textContent ?? ''))
    .join(' ');
  return boldText === text;
}

function normalizeSpaces(value: string): string {
  return value
    .replace(/&nbsp;|\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
